import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import "./BadgePage.css";

export default function BadgesPage() {
  const { userId } = useParams();

  const data = useQuery(
    api.progress.getBadges,
    userId
      ? { userId }
      : "skip"
  );

  if (data === undefined) {
    return (
      <div className="badges-page">
        <div className="badges-container">
          <div className="badges-header">
            <h1 className="badges-title">
              Badges & Milestones
            </h1>

            <p className="badges-subtitle">
              Loading badges...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="badges-page">
        <div className="badges-container">
          <div className="badges-header">
            <h1 className="badges-title">
              Badges & Milestones
            </h1>
          </div>

          <div className="badges-empty badges-empty--large">
            <h2 className="badges-empty-title">
              No badge data available
            </h2>

            <p className="badges-empty-text">
              Complete some activities to start earning badges.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const badges = Array.isArray(data.badges)
    ? data.badges
    : [];

  return (
    <div className="badges-page">
      <div className="badges-container">

        {/* HEADER */}

        <div className="badges-header">
          <p className="badges-eyebrow">
            Achievements
          </p>

          <h1 className="badges-title">
            Badges & Milestones
          </h1>

          <p className="badges-subtitle">
            Earn achievements by consistently cultivating
            better habits.
          </p>
        </div>

        {/* SUMMARY */}

        <div className="badges-summary-grid">

          <div className="badges-summary-card">
            <p className="badges-summary-label">
              Overall Score
            </p>

            <p className="badges-summary-value">
              {Number(data.overallScore ?? 0)}

              <span className="badges-summary-unit">
                /100
              </span>
            </p>
          </div>

          <div className="badges-summary-card">
            <p className="badges-summary-label">
              Completed Activities
            </p>

            <p className="badges-summary-value">
              {Number(data.completedActivities ?? 0)}
            </p>
          </div>

        </div>

        {/* BADGES */}

        {badges.length === 0 ? (
          <div className="badges-empty">
            <div className="badges-empty-icon">
              ✦
            </div>

            <h2 className="badges-empty-title">
              No badges yet
            </h2>

            <p className="badges-empty-text">
              Keep completing activities to unlock your
              first achievement.
            </p>
          </div>
        ) : (
          <div className="badges-grid">

            {badges.map((badge) => {
              const progress = Number(
                badge.progress ?? 0
              );

              const target = Number(
                badge.target ?? 0
              );

              const percentage =
                target > 0
                  ? Math.min(
                      Math.max(
                        (progress / target) * 100,
                        0
                      ),
                      100
                    )
                  : 0;

              return (
                <section
                  key={badge.id}
                  className={`badge-card ${
                    badge.unlocked
                      ? "badge-card--unlocked"
                      : ""
                  }`}
                >

                  {/* BADGE HEADER */}

                  <div className="badge-header">

                    <div
                      className={`badge-icon ${
                        badge.unlocked
                          ? "badge-icon--unlocked"
                          : "badge-icon--locked"
                      }`}
                    >
                      {badge.icon}
                    </div>

                    <div className="badge-heading">
                      <h2 className="badge-title">
                        {badge.title}
                      </h2>

                      {badge.unlocked ? (
                        <p className="badge-status badge-status--unlocked">
                          ✓ Unlocked
                        </p>
                      ) : (
                        <p className="badge-status badge-status--locked">
                          Locked
                        </p>
                      )}
                    </div>

                  </div>

                  {/* DESCRIPTION */}

                  <p className="badge-description">
                    {badge.description}
                  </p>

                  {/* PROGRESS */}

                  <div className="badge-progress-header">
                    <span className="badge-progress-label">
                      Progress
                    </span>

                    <span className="badge-progress-value">
                      {progress}/{target}
                    </span>
                  </div>

                  <div className="badge-progress-track">
                    <div
                      className={`badge-progress-fill ${
                        badge.unlocked
                          ? "badge-progress-fill--unlocked"
                          : "badge-progress-fill--locked"
                      }`}
                      style={{
                        width: `${percentage}%`,
                      }}
                    />
                  </div>

                  <div className="badge-percentage">
                    <span>
                      {Math.round(percentage)}%
                    </span>
                  </div>

                </section>
              );
            })}

          </div>
        )}

      </div>
    </div>
  );
}
